// components/Form2316Management.js
import React, { useState, useEffect, useCallback } from 'react';
import Form2316Form from './Form2316Form';
import Form2316List from './Form2316List';
import Form2316Summary from './Form2316Summary';
import form2316API from '../api/form2316API';
import { formatCurrency } from '../utils/numberUtils';

const Form2316Management = ({ client }) => {
    const [activeSection, setActiveSection] = useState('form2316');
    const [form2316Records, setForm2316Records] = useState([]);
    const [form2316Summary, setForm2316Summary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(null);
    const [availableYears, setAvailableYears] = useState([]);

    // Generate available years (current year and past 5 years)
    const generateYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 0; i < 6; i++) {
            years.push(currentYear - i);
        }
        return years;
    };

    // Fetch Form 2316 records
    const fetchForm2316Records = useCallback(async (year = null) => {
        try {
            setLoading(true);
            const params = year ? { year } : {};
            const response = await form2316API.getByClient(client._id, params);
            setForm2316Records(response.data?.records || []);
        } catch (error) {
            console.error('Error fetching Form 2316 records:', error);
            alert('Failed to load Form 2316 records: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [client._id]);

    // Fetch Form 2316 summary
    const fetchForm2316Summary = useCallback(async (year = null) => {
        try {
            const params = year ? { year } : {};
            const response = await form2316API.getSummary(client._id, params);
            setForm2316Summary(response.data);
        } catch (error) {
            console.error('Error fetching Form 2316 summary:', error);
        }
    }, [client._id]);

    // Fetch available years from API
    const fetchAvailableYears = useCallback(async () => {
        try {
            const response = await form2316API.getYears(client._id);
            const yearsFromAPI = response.data?.map(item => item.year) || [];
            
            if (yearsFromAPI.length > 0) {
                setAvailableYears(yearsFromAPI);
                if (!selectedYear) {
                    setSelectedYear(yearsFromAPI[0]);
                }
            } else {
                // Use generated years if no records yet
                const generatedYears = generateYears();
                setAvailableYears(generatedYears);
                if (!selectedYear) {
                    setSelectedYear(generatedYears[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching years:', error);
            const generatedYears = generateYears();
            setAvailableYears(generatedYears);
            if (!selectedYear) {
                setSelectedYear(generatedYears[0]);
            }
        }
    }, [client._id, selectedYear]);

    // Fetch data on mount and section changes
    useEffect(() => {
        if (activeSection === 'form2316' || activeSection === 'summary') {
            fetchAvailableYears();
        }
    }, [activeSection, fetchAvailableYears]);

    // Fetch records when year changes
    useEffect(() => {
        if (selectedYear) {
            fetchForm2316Records(selectedYear);
            fetchForm2316Summary(selectedYear);
        }
    }, [selectedYear, fetchForm2316Records, fetchForm2316Summary]);

    // Handler for Form 2316 updates
    const handleForm2316Updated = useCallback(async () => {
        await fetchForm2316Records(selectedYear);
        await fetchForm2316Summary(selectedYear);
        await fetchAvailableYears();
    }, [selectedYear, fetchForm2316Records, fetchForm2316Summary, fetchAvailableYears]);

    // Handler for Form 2316 deletion
    const handleForm2316Deleted = useCallback(async () => {
        await fetchForm2316Records(selectedYear);
        await fetchForm2316Summary(selectedYear);
        await fetchAvailableYears();
    }, [selectedYear, fetchForm2316Records, fetchForm2316Summary, fetchAvailableYears]);

    // Handler for Form 2316 creation
    const handleForm2316Added = useCallback(async () => {
        await fetchForm2316Records(selectedYear);
        await fetchForm2316Summary(selectedYear);
        await fetchAvailableYears();
    }, [selectedYear, fetchForm2316Records, fetchForm2316Summary, fetchAvailableYears]);

    // Calculate Form 2316 summary for current year
    const calculateForm2316Summary = useCallback(() => {
        const filteredRecords = selectedYear
            ? form2316Records.filter(r => r.year === selectedYear)
            : form2316Records;

        return filteredRecords.reduce((acc, record) => ({
            totalGrossCompensation: acc.totalGrossCompensation + (record.grossCompensationIncome || 0),
            totalTaxWithheld: acc.totalTaxWithheld + (record.taxWithheld || 0),
            totalTaxDue: acc.totalTaxDue + (record.taxDue || 0),
            totalTaxRefund: acc.totalTaxRefund + (record.taxRefund || 0),
            recordCount: acc.recordCount + 1,
            employeeCount: new Set([...acc.employeeSet, record.employeeTin || '']).size
        }), { 
            totalGrossCompensation: 0, 
            totalTaxWithheld: 0, 
            totalTaxDue: 0, 
            totalTaxRefund: 0,
            recordCount: 0,
            employeeSet: new Set()
        });
    }, [form2316Records, selectedYear]);

    // Function to export filtered records to CSV
    const exportToCSV = useCallback(() => {
        // Get filtered records based on selected year
        const filteredRecords = selectedYear
            ? form2316Records.filter(r => r.year === selectedYear)
            : form2316Records;

        if (filteredRecords.length === 0) {
            alert('No records to export for the selected year.');
            return;
        }

        // Define CSV headers for BIR Form 2316
        const headers = [
            'ID',
            'Employee Name',
            'Employee TIN',
            'Civil Status',
            'Exemption Status',
            'Basic Salary',
            'Overtime Pay',
            'Holiday Pay',
            '13th Month Pay',
            'Other Allowances',
            'Gross Compensation',
            'SSS Contributions',
            'PhilHealth Contributions',
            'Pag-IBIG Contributions',
            'Total Deductions',
            'Net Taxable Compensation',
            'Tax Due',
            'Tax Withheld',
            'Tax Refund',
            'Year',
            'Status',
            'Certificate Number',
            'Certificate Date',
            'Created Date'
        ];

        // Convert records to CSV rows
        const csvRows = filteredRecords.map(record => {
            const totalDeductions = (record.sssContributions || 0) + 
                                   (record.philhealthContributions || 0) + 
                                   (record.pagibigContributions || 0);
            
            const row = [
                record._id || '',
                `"${record.employeeName || ''}"`,
                record.employeeTin || '',
                record.employeeCivilStatus || '',
                record.employeeExemptionStatus || '',
                record.basicSalary ? `"${record.basicSalary.toFixed(2)}"` : '"0.00"',
                record.overtimePay ? `"${record.overtimePay.toFixed(2)}"` : '"0.00"',
                record.holidayPay ? `"${record.holidayPay.toFixed(2)}"` : '"0.00"',
                record.thirteenthMonthPay ? `"${record.thirteenthMonthPay.toFixed(2)}"` : '"0.00"',
                record.otherAllowances ? `"${record.otherAllowances.toFixed(2)}"` : '"0.00"',
                record.grossCompensationIncome ? `"${record.grossCompensationIncome.toFixed(2)}"` : '"0.00"',
                record.sssContributions ? `"${record.sssContributions.toFixed(2)}"` : '"0.00"',
                record.philhealthContributions ? `"${record.philhealthContributions.toFixed(2)}"` : '"0.00"',
                record.pagibigContributions ? `"${record.pagibigContributions.toFixed(2)}"` : '"0.00"',
                `"${totalDeductions.toFixed(2)}"`,
                record.netTaxableCompensation ? `"${record.netTaxableCompensation.toFixed(2)}"` : '"0.00"',
                record.taxDue ? `"${record.taxDue.toFixed(2)}"` : '"0.00"',
                record.taxWithheld ? `"${record.taxWithheld.toFixed(2)}"` : '"0.00"',
                record.taxRefund ? `"${record.taxRefund.toFixed(2)}"` : '"0.00"',
                record.year || '',
                record.status || 'Draft',
                record.certificateNumber || '',
                record.certificateDate ? new Date(record.certificateDate).toLocaleDateString() : '',
                record.createdAt ? new Date(record.createdAt).toLocaleDateString() : ''
            ];
            return row.join(',');
        });

        // Create CSV content
        const csvContent = [headers.join(','), ...csvRows].join('\n');

        // Create a blob and download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        // Generate filename
        const yearLabel = selectedYear ? selectedYear : 'All_Years';
        const filename = `Form2316_${client.name.replace(/\s+/g, '_')}_${yearLabel}_${new Date().toISOString().split('T')[0]}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        URL.revokeObjectURL(url);
        alert(`Exported ${filteredRecords.length} Form 2316 records to CSV.`);
    }, [form2316Records, selectedYear, client.name]);

    // Function to export summary to CSV
    const exportSummaryToCSV = useCallback(() => {
        const summary = calculateForm2316Summary();
        const filteredRecords = selectedYear
            ? form2316Records.filter(r => r.year === selectedYear)
            : form2316Records;

        if (filteredRecords.length === 0) {
            alert('No records to export for the selected year.');
            return;
        }

        // Define CSV headers for summary
        const headers = [
            'Year',
            'Client Name',
            'Total Records',
            'Total Employees',
            'Total Gross Compensation',
            'Total Tax Withheld',
            'Total Tax Due',
            'Total Tax Refund',
            'Average Tax Rate',
            'Export Date'
        ];

        // Create summary row
        const yearLabel = selectedYear ? selectedYear : 'All Years';
        const averageTaxRate = summary.totalGrossCompensation > 0 
            ? ((summary.totalTaxWithheld / summary.totalGrossCompensation) * 100).toFixed(2)
            : '0.00';
        
        const summaryRow = [
            yearLabel,
            client.name,
            summary.recordCount,
            summary.employeeCount,
            `"${summary.totalGrossCompensation.toFixed(2)}"`,
            `"${summary.totalTaxWithheld.toFixed(2)}"`,
            `"${summary.totalTaxDue.toFixed(2)}"`,
            `"${summary.totalTaxRefund.toFixed(2)}"`,
            `${averageTaxRate}%`,
            new Date().toLocaleDateString()
        ];

        // Create CSV content
        const csvContent = [headers.join(','), summaryRow.join(',')].join('\n');

        // Create a blob and download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        const yearFileLabel = selectedYear ? selectedYear : 'All_Years';
        const filename = `Form2316_Summary_${client.name.replace(/\s+/g, '_')}_${yearFileLabel}_${new Date().toISOString().split('T')[0]}.csv`;
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        URL.revokeObjectURL(url);
        alert('Exported Form 2316 summary to CSV.');
    }, [form2316Records, selectedYear, client.name, calculateForm2316Summary]);

    const form2316YearSummary = calculateForm2316Summary();
    const isAllYears = !selectedYear;

    const sections = [
        { id: 'form2316', name: 'Form 2316', description: 'Create and manage BIR Form 2316 records' },
        { id: 'summary', name: '2316 Summary', description: 'View Form 2316 summary and reports' },
    ];

    if (loading && form2316Records.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading Form 2316 records...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="border-b border-gray-200 pb-4">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Withholding Tax - Compensation (BIR Form 2316)</h2>
                        <p className="text-gray-600 mt-1">
                            Manage Withholding Tax on Compensation records for {client.name}
                        </p>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                        {/* Export Buttons - Only show when there are records */}
                        {form2316Records.length > 0 && (
                            <div className="flex items-center space-x-2">
                                <button
                                    type="button"
                                    onClick={exportToCSV}
                                    disabled={loading}
                                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                                    title="Export all records to CSV"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Export CSV
                                </button>
                                <button
                                    type="button"
                                    onClick={exportSummaryToCSV}
                                    disabled={loading}
                                    className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                                    title="Export summary to CSV"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Export Summary
                                </button>
                            </div>
                        )}
                        
                        {/* Year Selector */}
                        <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-500">Year:</span>
                            
                            {/* Year Selector */}
                            <select
                                value={selectedYear || ''}
                                onChange={(e) => {
                                    const year = e.target.value ? parseInt(e.target.value) : null;
                                    setSelectedYear(year);
                                }}
                                className="block rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-sm focus:border-teal-500 focus:ring-teal-500"
                                disabled={loading}
                            >
                                <option value="">All Years</option>
                                {availableYears.map(year => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                            
                            {/* Optional: Show "All Years" reset button */}
                            {selectedYear && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedYear(null)}
                                    className="text-xs text-teal-600 hover:text-teal-800"
                                >
                                    Reset
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 overflow-x-auto">
                    {sections.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            disabled={loading}
                            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeSection === section.id
                                    ? section.id === 'form2316' 
                                        ? 'border-teal-500 text-teal-600'
                                        : 'border-teal-500 text-teal-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 disabled:opacity-50'
                            }`}
                            title={section.description}
                        >
                            {section.name}
                            {loading && activeSection === section.id && (
                                <span className="ml-2 inline-block animate-pulse">↻</span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg border">
                {/* Form 2316 Section */}
                {activeSection === 'form2316' && (
                    <div>
                        <Form2316Form 
                            client={client} 
                            onRecordAdded={handleForm2316Added}
                            selectedYear={selectedYear}
                        />
                        {form2316Records.length > 0 && (
                            <div className="border-t border-gray-200 mt-6">
                                <div className="p-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="flex items-center space-x-4">
                                            <h4 className="text-lg font-semibold text-teal-700">
                                                {isAllYears ? 'Form 2316 Records' : `Form 2316 Records (${selectedYear})`}
                                            </h4>
                                            <div className="text-sm text-teal-600 bg-teal-50 px-3 py-1 rounded">
                                                {form2316YearSummary.recordCount} records • {form2316YearSummary.employeeCount} employees
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <div className="text-sm text-teal-600">
                                                Total Tax Withheld: <span className="font-semibold">{formatCurrency(form2316YearSummary.totalTaxWithheld)}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={exportToCSV}
                                                disabled={loading}
                                                className="px-3 py-1 text-xs bg-green-100 text-green-700 border border-green-300 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                                                title="Export filtered records to CSV"
                                            >
                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Export
                                            </button>
                                        </div>
                                    </div>
                                    <Form2316List 
                                        records={isAllYears 
                                            ? form2316Records
                                            : form2316Records.filter(record => record.year === selectedYear)
                                        }
                                        onRecordDeleted={handleForm2316Deleted}
                                        onRecordUpdated={handleForm2316Updated}
                                        showActions={true}
                                        client={client}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* 2316 Summary Section */}
                {activeSection === 'summary' && (
                    <div className="p-6">
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-teal-700">Form 2316 Summary</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Summary reports and analytics for Withholding Tax on Compensation records
                            </p>
                        </div>
                        
                        {form2316Records.length > 0 ? (
                            <Form2316Summary 
                                client={client}
                                records={form2316Records}
                                selectedYear={selectedYear}
                                yearSummary={form2316YearSummary}
                                summaryData={form2316Summary}
                                onExportCSV={exportToCSV}
                                onExportSummary={exportSummaryToCSV}
                            />
                        ) : (
                            <div className="text-center py-12">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No Form 2316 records found</h3>
                                <p className="mt-1 text-sm text-gray-500">Get started by creating Form 2316 records.</p>
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setActiveSection('form2316')}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                    >
                                        Go to Form 2316
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Form2316Management;